<template>
  <div ref='rootRef' class='outer-wrapper'>
    <Transition :duration='500'>
      <section v-if='filterDetails' class='show'>
        <div class='headline'>
          <span>
            <ChevronLeftIcon @click='toggleFilterDetails(null)' />
            {{ filterDetails.label }}
          </span>
          <button
            v-if='isFilterDetailsActiveOptions'
            class='button-text'
            @click='resetFilterDetails'
          >
            {{ tp('reset_filter') }}<CloseIcon />
          </button>
        </div>
        <div v-if='filterDetails.isSearch' class='b-input'>
          <input v-model='searchPhrase' type='text'>
        </div>
        <div class='options'>
          <NothingFound
            v-if='searchPhrase && !filterDetailsOptions.length'
            :title='tpSearch(`index.nothing_found.title.${filterDetails.name}`)'
            :note='tpSearch(`index.nothing_found.note`)'
          />
          <template v-else>
            <label
              v-for='option in filterDetailsOptions'
              :key='option.value'
              class='option'
            >
              <CheckboxV4
                :is-checked='option.isChecked'
                @update:is-checked='updateFilterOption(filterDetails.name, option, $event)'
              />
              {{ option.label }}
              <span
                v-if='option.markerClass'
                :class='option.markerClass'
                :style='option.markerStyle'
              />
            </label>
          </template>
        </div>
      </section>
    </Transition>
    <div v-if='filterDetails' class='submit-container'>
      <button class='submit' @click='toggleFilterDetails(null)'>
        {{ tp('submit_filter') }}
      </button>
    </div>
    <div v-else class='submit-container'>
      <button class='submit' @click='close'>
        {{ tp('submit_filters', { count: storagesCount }) }}
      </button>
    </div>

    <Transition :duration='500'>
      <section v-if='!filterDetails' class='index'>
        <div class='headline'>
          {{ tp('headline') }}
          <button
            v-if='allActiveOptions.length > 1'
            class='button-text'
            @click='resetFilters'
          >
            {{ tp('reset_filters') }}<CloseIcon />
          </button>
        </div>
        <div v-if='allActiveOptions.length' class='options'>
          <LabelButton
            v-for='[filter, option] in allActiveOptions'
            :key='option.value'
            :is-active='true'
            :is-reset='isOptionChangeAllowed(filter, option, false)'
            @click='deselectOption(option)'
            @reset='deselectOption(option)'
          >
            {{ option.label }}
            <span
              v-if='option.markerClass'
              :class='option.markerClass'
              :style='option.markerStyle'
            />
          </LabelButton>
        </div>

        <div>
          <section class='filters'>
            <FilterBlock
              v-for='filter in filters'
              :key='filter.name'
              v-bind='filter'
              @filter:update='updateFilterOption($event.filterName, $event.option, $event.isChecked)'
              @filter:details='toggleFilterDetails(filter)'
            />
          </section>
        </div>
      </section>
    </Transition>
  </div>
</template>

<script>
export default {
  name: 'FiltersModal'
};
</script>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import flatMap from 'lodash/flatMap';

import CloseIcon from '@/../images/vue/storages_map-v4/filters_modal/close.svg?inline';
import ChevronDownIcon from '@/../images/vue/storages_map-v4/filters_modal/chevron-down.svg?inline';
import ChevronLeftIcon from '@/../images/vue/storages_map-v4/filters_modal/chevron-left.svg?inline';
import { prepared } from '@/helpers/i18n';

import CheckboxV4 from '../../components/checkbox_v4';
import LabelButton from '../../components/label_button';
import TransitionHeight from '../../components/transition_height';
import FilterBlock from './filter_block';
import NothingFound from '../nothing_found';

const props = defineProps({
  filters: { type: Array, required: true },
  storagesCount: { type: Number, required: true }
});
const emit = defineEmits(['filter:update', 'filters:reset', 'modal:hide']);
const tp = prepared('frontend.storages_map-v4.filters_modal.index');
const tpSearch = prepared('frontend.storages_map-v4.dropdown_filter');

const rootRef = ref(null);
const filterDetails = ref(null);
const searchPhrase = ref('');

const allActiveOptions = computed(() => (
  flatMap(props.filters, filter => (
    filter.options
      .filter(({ isChecked }) => isChecked)
      .map(option => [filter, option])
  ))
));

const isFilterDetailsActiveOptions = computed(() => (
  filterDetails.value
    ?.options
    ?.some(({ isChecked }) => isChecked)
));

function resetFilters() {
  emit('filters:reset');
  close();
}

function close() {
  emit('modal:hide');
}

function deselectOption(option) {
  const filter = props.filters.find(filter => (
    filter.options.find(filterOption => filterOption === option)
  ));
  updateFilterOption(filter.name, option, false);
}

function isOptionChangeAllowed(filter, option, isChecked) {
  if ((option.isChecked && isChecked) || (!option.isChecked && !isChecked)) {
    return false;
  }
  // prevent uncheck already selected radio button
  if (filter.isRadio && !isChecked &&
      filter.options.filter(({ isChecked }) => isChecked).length === 1
  ) {
    return false;
  }

  return true;
}

function resetFilterDetails() {
  const options = filterDetails.value.options.map(option => ({
    ...option,
    isChecked: isOptionChangeAllowed(filterDetails.value, option, false) ?
      false :
      option.isChecked
  }));

  emit('filter:update', {
    filterName: filterDetails.value.name,
    attributes: { options }
  });
  filterDetails.value = null;
}

const filterDetailsOptions = computed(() => {
  if (!searchPhrase.value) {
    return filterDetails.value.options;
  }

  const fixedPhrase = searchPhrase.value.toLowerCase().trim();
  return filterDetails.value.options.filter(({ label }) => (
    label.toLowerCase().includes(fixedPhrase)
  ));
});

function updateFilterOption(filterName, option, isChecked) {
  const filter = props.filters.find(({ name }) => name === filterName);

  if (!isOptionChangeAllowed(filter, option, isChecked)) {
    return;
  }

  const options = filter.options.map(filterOption => ({
    ...filterOption,
    isChecked: option === filterOption ?
      isChecked :
      (isChecked && filter.isRadio ? false : filterOption.isChecked)
  }));

  emit('filter:update', {
    filterName: filter.name,
    attributes: { options }
  });
}

function toggleFilterDetails(filter) {
  filterDetails.value = filter;
  searchPhrase.value = '';
}

watch(() => filterDetails.value, () => (
  requestAnimationFrame(() => (
    rootRef.value
      ?.parentNode
      ?.scrollTo({ top: 0, behavior: 'smooth' })
  ))
));
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/button_v4'
@import 'app/packs/stylesheets/mixins/ltr_rtl'
@import 'app/packs/stylesheets/mixins/responsive'
@import 'app/packs/stylesheets/mixins/typography'

// added in order to help position section properly during transition
.outer-wrapper
  position: relative

section
  display: flex
  flex-direction: column
  gap: rem4(24px)
  position: relative
  width: 100%

.headline
  +h4
  color: #000
  letter-spacing: rem4(-0.48px)
  display: flex
  gap: rem4(24px)
  justify-content: space-between

  span
    align-items: center
    display: flex
    gap: rem4(8px)

    svg
      color: var(--gray-500)

      +gte_laptop
        transition: color var(--transition-duration)

        &:hover
          color: var(--gray-700)

      &:active
        color: var(--gray-700)

.button-text,
::v-deep(.button-text)
  +p4
  color: var(--gray-500)
  display: flex
  gap: rem4(8px)
  align-items: center

  +gte_laptop
    transition: color var(--transition-duration)

    &:hover
      color: var(--gray-700)

  &:active
    color: var(--gray-700)

.submit-container
  background: #fff
  border-radius: rem4(16px) rem4(16px) 0 0
  bottom: 0
  box-shadow: 0 0 rem4(24.944px) 0 rgba(3, 3, 3, 0.10)
  left: 0
  padding: rem4(24px)
  position: fixed
  width: 100%
  z-index: 1
  -webkit-transform: translateZ(0) // fixes safari bug https://stackoverflow.com/questions/55023088/fixed-position-in-safari

  .submit
    +button_v4('primary', 'sm')
    height: rem4(46px) !important
    width: 100%

.index,
.show
  padding-bottom: rem4(94px) // offset for submit-container
  height: 100%

.index
  .options
    display: flex
    flex-wrap: wrap
    gap: rem4(12px)

  ::v-deep(.label-button)
    +p5
    height: rem4(32px)
    letter-spacing: normal

    .separator
      display: none

      & + svg
        width: rem4(16px)

  .filters
    transition: height var(--transition-duration)

.show
  .options
    display: flex
    flex-direction: column
    flex-grow: 1
    gap: rem4(8px)

  .option
    +p4
    align-items: center
    color: var(--gray-900)
    display: flex
    gap: rem4(8px)
    height: rem4(32px)

  .b-input
    margin-bottom: 0

// animations
.show,
.index
  &.v-enter-active,
  &.v-leave-active
    transition: all 500ms
    position: absolute

.show
  &.v-enter-from,
  &.v-leave-to
    transform: translateX(calc(100% + var(--modal-padding-x)))

  &.v-enter-to,
  &.v-leave-from
    transform: translateX(0)

.index
  &.v-enter-from,
  &.v-leave-to
    transform: translateX(calc(-100% - var(--modal-padding-x)))

  &.v-enter-to,
  &.v-leave-from
    transform: translateX(0)
</style>
